/**
 * AdminApiError — 统一包装后端 envelope 失败响应
 *
 * 业务码分支永远按 code 字符串判断，禁按 message 文案匹配（dev-docs 10-API响应格式 §6）。
 * 显示文案：admin 中文-only UI，按 code 本地化为中文（error-messages.ts），未登记回退后端 message；
 * 校验失败带 `errors[]` 时以字段级原因为准（通用文案说不出「哪个字段、为什么」）。
 */

import { zhErrorMessage } from './error-messages';

export type AdminErrorBody = {
  code: string;
  message: string;
  requestId: string;
  errors?: Array<{ field: string; message: string }>;
  details?: Record<string, unknown>;
};

/** 展示文案里最多列几条字段错误——再多运营也读不完，余量折成「等 N 项」 */
const MAX_FIELD_ERRORS_SHOWN = 3;
/**
 * 单条 / 拼接后的长度上限。
 *
 * 服务端会把用户输入回显进 `errors[].message`（如推送模板把正文里的原始占位符片段拼进报错），
 * 单条可达数百字符且可能整串无空白。toast 宽 360px 且不换行，这种串会直接撑破布局。
 */
const MAX_FIELD_MESSAGE_CHARS = 120;
const MAX_DETAIL_CHARS = 200;

/** 带字段原因时 VALIDATION_FAILED 用的短前缀：原因本身已是指引，完整文案的「请检查后重试」成了噪音 */
const VALIDATION_PREFIX_WITH_DETAIL = '输入校验失败';

/** 超长截断，保留可读前缀 */
function clamp(text: string, max: number): string {
  return text.length <= max ? text : `${text.slice(0, max)}…`;
}

/** 含中文即视为服务端为运营写的展示文案（英文默认 message 不含） */
const CJK_CHAR = /[一-鿿]/;

/** 标识符字符：字段名多是 snake_case，按这个判词边界 */
const IDENTIFIER_CHAR = /[A-Za-z0-9_]/;

/**
 * message 里是否**以独立词**提到了该字段名。
 *
 * 不能用裸子串判断：短字段名会误命中单词内部（field `id` 撞上 message 里的 `Invalid`），
 * 于是本该补上的字段名被当成「已提到」而吞掉，运营反而不知道该改哪个字段。
 * 逐个出现位置检查左右邻字符，避开对任意字段名做正则转义。
 */
function mentionsField(message: string, field: string): boolean {
  let from = 0;
  let at = message.indexOf(field, from);
  while (at >= 0) {
    const before = at > 0 ? (message[at - 1] ?? '') : '';
    const after = message[at + field.length] ?? '';
    if (!IDENTIFIER_CHAR.test(before) && !IDENTIFIER_CHAR.test(after)) return true;
    from = at + 1;
    at = message.indexOf(field, from);
  }
  return false;
}

/**
 * 把 `errors[]` 归一成一句可读的字段原因；无可用条目返回空串。
 *
 * 这里对形状**逐项防御**而非信任契约：envelope 在 `client.ts` 是无 Zod 的裸 `as` 断言，
 * `errors` 的类型只是声明、不是保证。而本函数跑在 `AdminApiError` 构造函数里——一旦对非数组
 * 取 `slice`、或对非字符串取 `includes` 抛 TypeError，抛出的就不再是 `AdminApiError`，
 * 会绕过所有 `err instanceof AdminApiError ? … : 兜底` 的消费方与重试分类，比不展示原因更糟。
 */
function formatFieldErrors(errors: unknown): string {
  if (!Array.isArray(errors)) return '';
  const parts: string[] = [];
  let valid = 0;
  for (const entry of errors) {
    if (!entry || typeof entry !== 'object') continue;
    const { field, message } = entry as { field?: unknown; message?: unknown };
    const msg = typeof message === 'string' ? message.trim() : '';
    if (msg === '') continue;
    valid += 1;
    // 只为要展示的前几条做字符串拼接：畸形超大数组不该在 Error 构造函数里做 O(n) 主线程工作
    if (parts.length >= MAX_FIELD_ERRORS_SHOWN) continue;
    const name = typeof field === 'string' ? field.trim() : '';
    // 服务端 message 常已含字段名（如「STRIPE bonus_amount 低于…」），再加前缀只会重复
    const text = name !== '' && !mentionsField(msg, name) ? `${name}：${msg}` : msg;
    parts.push(clamp(text, MAX_FIELD_MESSAGE_CHARS));
  }
  if (parts.length === 0) return '';
  const shown = clamp(parts.join('；'), MAX_DETAIL_CHARS);
  return valid > MAX_FIELD_ERRORS_SHOWN ? `${shown}（等 ${valid} 项）` : shown;
}

/**
 * 生成展示文案。
 *
 * 业务码的具体原因写在 code 对应的中文文案里，但 `VALIDATION_FAILED` 是唯一例外——它的通用文案
 * 说不出「哪个字段、为什么」，可执行信息全在 `errors[]`（契约：errors[] 仅 VALIDATION_FAILED 出现，
 * 见 dev-docs 01-通用约定/10-API响应格式）。只按 code 本地化会把这层信息丢掉，运营只看到
 * 「输入校验失败，请检查后重试」，无从知道该改哪个字段。
 *
 * **本地化前缀始终保留**：服务端 validation message 绝大多数是英文（`'must be a positive integer'`
 * 之类），若拿它整个顶替中文文案，中文-only 后台会直接吐英文。故拼成「中文前缀：字段原因」——
 * 前缀保证运营看得懂出了哪类问题，后半段补上「哪个字段、为什么」。
 *
 * **只有 `VALIDATION_FAILED` 拼字段原因**：契约里 `errors[]` 仅此码出现，其余码的中文文案是
 * 客户端策划的完整语义，往后面贴服务端原文没有收益、只扩大信任面——鉴权类码尤其敏感，
 * 登录 / 邀请 / 重置密码等未鉴权页面会把 message 原样渲染给访问者。
 *
 * **`RESOURCE_CONFLICT` 的中文 message 整句顶替**：这个系统码承载大量业务冲突（如「归档项目不能
 * 添加素材」），通用文案「操作冲突，请刷新后重试」既说不出原因、还会误导运营反复刷新重试。服务端
 * 为运营写的中文原因就是可执行指引，直接展示；英文默认值（`Resource conflict`）仍走本地化文案。
 */
function buildDisplayMessage(body: AdminErrorBody): string {
  if (body.code === 'RESOURCE_CONFLICT') {
    const message = typeof body.message === 'string' ? body.message.trim() : '';
    if (CJK_CHAR.test(message)) return clamp(message, MAX_DETAIL_CHARS);
  }
  const base = zhErrorMessage(body.code, body.message);
  if (body.code !== 'VALIDATION_FAILED') return base;
  const detail = formatFieldErrors(body.errors);
  return detail === '' ? base : `${VALIDATION_PREFIX_WITH_DETAIL}：${detail}`;
}

export class AdminApiError extends Error {
  readonly code: string;
  readonly httpStatus: number;
  readonly requestId: string;
  readonly errors?: Array<{ field: string; message: string }>;
  readonly details?: Record<string, unknown>;

  constructor(httpStatus: number, body: AdminErrorBody) {
    // 显示文案本地化为中文（按 code），未登记回退后端 message；带 errors[] 时补上字段级原因
    super(buildDisplayMessage(body));
    this.name = 'AdminApiError';
    this.code = body.code;
    this.httpStatus = httpStatus;
    this.requestId = body.requestId;
    this.errors = body.errors;
    this.details = body.details;
  }
}

/** HTTP 已返回但 body 不是可识别的 admin-api envelope；写请求结果必须按不明确处理。 */
export class UnexpectedAdminApiResponseError extends AdminApiError {
  constructor(httpStatus: number) {
    super(httpStatus, {
      code: 'INTERNAL_ERROR',
      message: `Unexpected response: ${httpStatus}`,
      requestId: '',
    });
    this.name = 'UnexpectedAdminApiResponseError';
  }
}

/**
 * 请求根本没拿到 HTTP 响应（DNS 失败 / 连接被阻断 / 断网 / 浏览器扩展拦截 / CORS 拒绝）。
 *
 * Why 继承 AdminApiError：调用方普遍以 `err instanceof AdminApiError ? err.message : '<兜底文案>'`
 * 决定展示文案，网络失败若只是裸 TypeError 会一律落到各页兜底文案（如登录页「登录失败，请重试」），
 * 把「连不上服务器」误显示成「操作失败」——运营据此反复重试 / 误以为密码错，排障方向被带偏。
 *
 * `NETWORK_UNREACHABLE` 是**前端本地码**，不是后端 dev-docs 错误码字典条目（服务端从未返回它）；
 * `httpStatus = 0` 表示无 HTTP 响应，故不会命中调用方按 401 / 422 分支的既有逻辑：
 * 401 自登出不误踢、批量写按 `ambiguous`（确实不知服务端是否已执行）而非 `safe-to-retry`。
 */
export class AdminNetworkError extends AdminApiError {
  constructor(cause?: unknown) {
    super(0, { code: 'NETWORK_UNREACHABLE', message: 'Network request failed', requestId: '' });
    this.name = 'AdminNetworkError';
    this.cause = cause;
  }
}

/**
 * 请求被中止 —— 分两类，因为运营该做的事完全不同。
 *
 * 共同点：都**没拿到 HTTP 响应**（`httpStatus = 0`），故不会命中调用方按 401 / 422 分支的既有逻辑。
 * 与 `AdminNetworkError` 并列而不复用它：那条的语义是「连不上服务器」，会让运营去查网络；
 * 而超时更可能是服务端慢、主动取消是运营自己点的，报成「无法连接服务器」都是误导。
 *
 * **`serverStateUnknown` 是本类存在的主要理由**：中止只停止了**本地等待**，请求可能已经送达并落库。
 * 写请求（非 GET）中止后 UI **绝不能显示「已撤销 / 未保存」** —— 那是替服务端做一个前端无从知道的
 * 判断；运营据此重试会造成重复写入（新建类端点尤甚）。正确姿势是提示「结果未知，请刷新确认」。
 */
export class AdminRequestAbortedError extends AdminApiError {
  /** 请求是否可能已在服务端生效：非 GET 一律 true —— 中止点在本地，服务端处理与否不可知 */
  readonly serverStateUnknown: boolean;

  constructor(
    code: 'REQUEST_TIMEOUT' | 'REQUEST_TIMEOUT_READ' | 'REQUEST_CANCELED',
    message: string,
    method: string,
  ) {
    super(0, { code, message, requestId: '' });
    this.name = 'AdminRequestAbortedError';
    this.serverStateUnknown = method.toUpperCase() !== 'GET';
  }
}

/** 超时：本地等待到点，服务端可能仍在处理。 */
export class AdminRequestTimeoutError extends AdminRequestAbortedError {
  constructor(method: string, timeoutMs: number) {
    const write = method.toUpperCase() !== 'GET';
    super(
      write ? 'REQUEST_TIMEOUT' : 'REQUEST_TIMEOUT_READ',
      `Request timed out after ${timeoutMs}ms`,
      method,
    );
    this.name = 'AdminRequestTimeoutError';
  }
}

/** 主动取消：调用方（页面离开 / 用户点取消）传入的 signal 触发。 */
export class AdminRequestCanceledError extends AdminRequestAbortedError {
  constructor(method: string) {
    super('REQUEST_CANCELED', 'Request canceled by caller', method);
    this.name = 'AdminRequestCanceledError';
  }
}
